/**
 * Utility functions for temperature conversion and formatting
 */

export type TemperatureUnit = 'celsius' | 'fahrenheit';

/**
 * Convert Celsius to Fahrenheit
 */
export function celsiusToFahrenheit(celsius: number): number {
  return Math.round((celsius * 9/5) + 32);
}

/**
 * Convert Fahrenheit to Celsius
 */
export function fahrenheitToCelsius(fahrenheit: number): number {
  return Math.round((fahrenheit - 32) * 5/9);
}

/**
 * Parse temperature string and extract numeric value
 * Handles formats like "25°C", "77°F", "25", etc.
 */
export function parseTemperature(tempStr: string): number {
  const match = tempStr.match(/-?\d+/);
  return match ? parseInt(match[0]) : 20;
}

/**
 * Format temperature with unit symbol
 */
export function formatTemperature(temp: number, unit: TemperatureUnit): string {
  const symbol = unit === 'fahrenheit' ? '°F' : '°C';
  return `${temp}${symbol}`;
}

/**
 * Convert temperature string from Celsius to user's preferred unit
 */
export function convertTemperatureString(
  tempStr: string, 
  targetUnit: TemperatureUnit
): string {
  if (targetUnit === 'celsius') return tempStr;
  
  const celsius = parseTemperature(tempStr);
  const fahrenheit = celsiusToFahrenheit(celsius);
  return formatTemperature(fahrenheit, 'fahrenheit');
}

/**
 * Convert weather data object to user's preferred temperature unit
 */
export function convertWeatherData(weatherData: any, targetUnit: TemperatureUnit): any {
  if (!weatherData || targetUnit === 'celsius') return weatherData;
  
  return {
    ...weatherData,
    current: weatherData.current ? {
      ...weatherData.current,
      temp: convertTemperatureString(weatherData.current.temp, targetUnit)
    } : undefined,
    daily: weatherData.daily ? weatherData.daily.map((day: any) => ({
      ...day,
      high: convertTemperatureString(day.high, targetUnit),
      low: convertTemperatureString(day.low, targetUnit),
      feelsLikeHigh: day.feelsLikeHigh ? convertTemperatureString(day.feelsLikeHigh, targetUnit) : undefined,
      feelsLikeLow: day.feelsLikeLow ? convertTemperatureString(day.feelsLikeLow, targetUnit) : undefined,
      // Legacy support for single feelsLike
      feelsLike: day.feelsLike ? convertTemperatureString(day.feelsLike, targetUnit) : undefined
    })) : []
  };
}
